// app 1.4.54 (Alexander, 20 Oct) — the multi-currency M® Note, the app's side.
//
// A series holds one token, so the series letter names the note's currency (notes/serial.ts
// SERIES). Every surface that prints a note — the take page, the gate, the Sending and sent
// cards, the rows, the chat card and its picture — reads the currency from the series and
// prints the amount in it: $20, €20, £20. Nothing here knows which currencies have a series;
// SERIES does, and the cash session adds a letter when a EURC series is deployed (sync
// 2026-10-20T1055Z; series C and D, 1.4.55).
import { BASE_CURRENCY, currency, formatFx } from "./fx";
import { formatUsdShort } from "./money";
import { noteSource } from "./notes/notes";

/** "$20", "$1.67", "€20", "€1.67", "₩12,000" — the amount as a note's face prints it. */
export function formatNoteAmount(code: string | undefined, n: number): string {
  const c = code ?? BASE_CURRENCY;
  if (c === BASE_CURRENCY) return formatUsdShort(n);
  const v = Number.isFinite(n) ? n : 0;
  const s = formatFx(c, v);
  return v < 10 ? s : s.replace(/\.00$/, ""); // as formatUsdShort: $1.00–$9.00 keep their cents, $10 and up drop .00
}

/** The currencies this build can issue a note in (1.4.55: the note source knows — the series VITE_NOTES_ADDRESS names and every series marked `issue`). */
export function noteCurrencies(): string[] {
  try { return noteSource().issuingCurrencies(); } catch { return []; }
}

export function canIssueNoteIn(code: string): boolean {
  return noteCurrencies().includes(code);
}

/** The money in a sentence — "To send euro", "your dollars", "your pounds" (1.4.56: "british pound" lower-cased was wrong). */
const WORDS: Record<string, string> = {
  USD: "dollars", EUR: "euro", GBP: "pounds", CHF: "francs", CAD: "Canadian dollars", AUD: "Australian dollars",
  MXN: "pesos", BRL: "reais", ZAR: "rand", SEK: "kronor", KRW: "won",
};
export function moneyWord(code: string): string {
  return WORDS[code] ?? currency(code).name;
}

/** Why a note can't be in this currency yet: "" when it can. */
export function noteCurrencyBlocked(code: string): string {
  if (canIssueNoteIn(code)) return "";
  const name = moneyWord(code);
  const only = noteCurrencies();
  if (only.length === 1 && only[0] === BASE_CURRENCY) return `M® Notes are in dollars. To send ${name}, pick a contact.`;
  return `M® Notes aren't in ${name} yet. To send ${name}, pick a contact.`;
}

/** The currency of a sent note's record. `SentNote` carries none yet (the cash session's `notes.ts`; asked for in the
 *  20 Oct sync); a record without one is a dollar note, as every note issued before a second series is. */
export function sentCurrency(rec: object): string {
  const c = (rec as { currency?: unknown }).currency;
  return typeof c === "string" && c ? c : BASE_CURRENCY;
}
